import { AbstractBuildstep } from "@project/emerge";
import type {
  AbstractPartDefinition,
  DragConstraint,
  DragUpdateOptions,
  DragUpdateResult,
  SerializableVector3,
} from "@project/emerge-types";
import { Sides } from "@project/emerge-types";
import find from "lodash/find";
import * as THREE from "three";
import { EdgeIndex } from "../enum/EdgeIndex";
import { EdgeType } from "../enum/EdgeType";
import { LTCaseUserOption } from "../enum/LTCaseUserOption";
import { PanelType } from "../enum/PanelType";
import { LTPartType } from "../enum/PartType";
import { ROLLER_TYPE } from "../enum/ROLLER_TYPE";
import { VariableOptionType } from "../enum/VariableOptionType";
import type { FlightCaseBlueprint } from "../interfaces/FlightCaseBlueprint";
import type { FlightCaseSettings } from "../interfaces/FlightCaseSettings";
import type { PanelColor } from "../interfaces/PanelColor";
import type { RollerBoardInfos } from "../interfaces/RollerBoardInfos";
import type {
  CreeperRollerType,
  EdgeRollerType,
  RollerBoardProperties,
  RollerType,
} from "../interfaces/RollerBoardProperties";
import type { RollerPanelProperties } from "../interfaces/RollerPanelProperties";
import type { RollerDefinition } from "../interfaces/RollerProperties";
import type { TrayMaterialType } from "../interfaces/TrayMaterialType";
import {
  buildCreeperResolutionSource,
  getHydratedLegacyCreeperWheel,
  isCreeperBundleMeshes,
  mergeCreeperCasterWithSourceMetadata,
  resolveSingleCasterFromNested,
} from "../settings/hydrateImportedCreeperRollers";
import { DefaultGroupName } from "../structure/Rect";
import { RectSizeHelper } from "../structure/RectSizeHelper";
import { OptionDBInstance } from "../user_options/options_db";
import { gridStepFromOptions, roundToDragGrid } from "../utils/dragGrid";

// --- Dependency Data Interfaces ---

interface BaseRollerStepDependency {
  partId: string;
  groupName: string;
  buildstepName: string;
}

interface RollerPanelDependencyData extends BaseRollerStepDependency {
  partKind: "RollerPanel";
  type: LTPartType.Panel;
  panelType: PanelType.RollerBoard;
  material: TrayMaterialType;
  color: PanelColor;
  strength: number;
  note: string;
  braked: boolean; // From settings.exteriorOptions.rollers.board.braked
  size: THREE.Vector2;
  position: THREE.Vector3;
  side: Sides.BOTTOM;
  n: number; // Index for multiple panels
  // Activate-only constraint: ⌘/Ctrl-click opens the roller board popup; not movable.
  dragConstraint?: DragConstraint;
  settingsContext?: Record<string, unknown>;
}

interface RollerDependencyData extends BaseRollerStepDependency {
  partKind: "Roller";
  type: LTPartType.Roller;
  partSelection: RollerType | CreeperRollerType; // Standard or Creeper roller config
  position: THREE.Vector3;
  side: Sides; // Widened type from Sides.BOTTOM | Sides.LEFT | Sides.RIGHT
  rotation?: THREE.Euler; // Optional: Pre-calculated for edge rollers
  isSupplyPart: true;
  // Drag constraint: single-panel rollers move on the bottom surface (2D),
  // split-panel rollers move along the board-width axis (1D).
  dragConstraint?: DragConstraint;
  settingsContext?: Record<string, unknown>;
}

type RollerStepDependencyData =
  | RollerPanelDependencyData
  | RollerDependencyData;

// --- Refactored Build Step ---

export class RollerBuildStep extends AbstractBuildstep<
  FlightCaseBlueprint,
  FlightCaseSettings,
  AbstractPartDefinition<string>,
  RollerStepDependencyData // Dependency data union type
> {
  constructor() {
    super("RollerBuildStep");
  }

  // Helper: get base rect and size helper
  private getBaseRectAndSizeHelper(
    blueprint: FlightCaseBlueprint,
    settings: FlightCaseSettings,
  ) {
    const baseRect = find(blueprint.rects, {
      side: Sides.BOTTOM,
      group: DefaultGroupName,
    });

    if (!baseRect) {
      console.warn(
        "RollerBuildStep: Base bottom rect not found. Skipping roller generation.",
      );
      return null;
    }

    const sizeHelper = new RectSizeHelper(baseRect, false);
    const { profiles, strength } = settings.exteriorOptions;

    for (const edge of [EdgeIndex.BOTTOM, EdgeIndex.TOP]) {
      if (baseRect.edges[edge]?.type === EdgeType.FROMCUT) {
        sizeHelper.expand(edge, 40 - profiles.extra / 2);
      } else if (baseRect.edges[(edge + 2) % 4]?.type === EdgeType.FROMCUT) {
        sizeHelper.shrink((edge + 2) % 4, strength);
      }
    }

    return { baseRect, sizeHelper };
  }

  // Helper: create panel material
  private createPanelMaterial(
    board: RollerBoardProperties,
    rollers: CreeperRollerType,
  ): TrayMaterialType {
    return {
      description: rollers.description ?? rollers.name,
      name: rollers.name,
      strength: board.strength,
      kind: LTCaseUserOption.TrayMaterialType,
      deleted: false,
    };
  }

  // Helper: create panel dependency
  private createPanelDependency(args: {
    panelId: string;
    panelMaterial: TrayMaterialType;
    holzbraunColor: PanelColor;
    board: RollerBoardProperties;
    panelSize: THREE.Vector2;
    panelPosition: THREE.Vector3;
    n: number;
    dragConstraint?: DragConstraint;
  }): RollerPanelDependencyData {
    const {
      panelId,
      panelMaterial,
      holzbraunColor,
      board,
      panelSize,
      panelPosition,
      n,
      dragConstraint,
    } = args;
    return {
      partId: panelId,
      partKind: "RollerPanel",
      type: LTPartType.Panel,
      panelType: PanelType.RollerBoard,
      groupName: DefaultGroupName,
      buildstepName: this.buildstepName,
      material: panelMaterial,
      color: holzbraunColor,
      strength: panelMaterial.strength,
      note: "Rollbrett",
      braked: board.braked,
      size: panelSize,
      position: panelPosition,
      side: Sides.BOTTOM,
      n,
      dragConstraint,
    };
  }

  // Helper: create roller dependency
  private createRollerDependency(
    rollerId: string,
    roller: CreeperRollerType | EdgeRollerType,
    rollerPosition: THREE.Vector3,
    side: Sides = Sides.BOTTOM,
    rotation?: THREE.Euler,
    dragConstraint?: DragConstraint,
    settingsContext?: Record<string, unknown>,
  ): RollerDependencyData {
    // Rollers now always have a single 'default' mesh
    return {
      partId: rollerId,
      partKind: "Roller",
      type: LTPartType.Roller,
      groupName: DefaultGroupName,
      buildstepName: this.buildstepName,
      partSelection: roller,
      position: rollerPosition,
      side,
      rotation,
      isSupplyPart: true,
      dragConstraint,
      settingsContext,
    };
  }

  /** Synthetic board (legacy exterior rollbrett) has no editable variableOption. */
  private static readonly LEGACY_BOARD_UUID = "legacy_rollbrett";

  /**
   * Surface (2D) drag constraint for a single-panel roller. The roller stays in
   * its own quadrant (sign-limited bounds) so the symmetric inset mapping
   * round-trips 1:1 with the rebuild and the part doesn't cross the board center.
   */
  private static rollerSurfaceConstraint(args: {
    boardUuid: string;
    width: number;
    depth: number;
    planZ: number;
    y: number;
    rollerWidth: number;
    signX: number;
    signZ: number;
  }): DragConstraint {
    const { boardUuid, width, depth, planZ, y, rollerWidth, signX, signZ } =
      args;
    const halfX = Math.max(0, width / 2 - rollerWidth / 2);
    const halfZ = Math.max(0, depth / 2 - rollerWidth / 2);
    const xMin = signX < 0 ? -halfX : 0;
    const xMax = signX < 0 ? 0 : halfX;
    const zMin = signZ < 0 ? planZ - halfZ : planZ;
    const zMax = signZ < 0 ? planZ : planZ + halfZ;

    // Diagonal (45°) snap line running from the quadrant corner toward the
    // board center. Moving along it keeps side inset == depth inset (equal
    // margins). The corner is where both insets are 0 (|x|=halfX, |z|=halfZ);
    // each unit of inset moves the position by 1mm on X and Z, so the world
    // segment length is min(halfX, halfZ) * sqrt(2) before it crosses a center
    // axis into the neighbouring quadrant.
    const SQRT2 = Math.SQRT2;
    const cornerX = signX * halfX;
    const cornerZ = planZ + signZ * halfZ;
    const diagonalLength = Math.min(halfX, halfZ) * SQRT2;
    const diagonalSnaps =
      diagonalLength > 0
        ? [
            {
              origin: [cornerX, y, cornerZ] as SerializableVector3,
              direction: [
                -signX / SQRT2,
                0,
                -signZ / SQRT2,
              ] as SerializableVector3,
              length: diagonalLength,
              label: "Diagonal",
            },
          ]
        : undefined;

    return {
      type: "surface",
      planeNormal: [0, 1, 0],
      bounds: {
        min: [xMin, y, zMin],
        max: [xMax, y, zMax],
      },
      snapAnchors: [{ position: [0, y, planZ], label: "Center" }],
      diagonalSnaps,
      sourceOptionUuid: boardUuid,
      optimistic: true,
    };
  }

  /**
   * Edge (1D) drag constraint for a split-panel roller along the board-width
   * axis. Sign-limited to the roller's half so `rollerDistanceFromEdge` maps 1:1.
   */
  private static rollerEdgeConstraint(args: {
    boardUuid: string;
    axis: "x" | "z";
    boardWidth: number;
    rollerWidth: number;
    sign: number;
    fixed: number; // the fixed (non-axis) horizontal coordinate
    y: number;
  }): DragConstraint {
    const { boardUuid, axis, boardWidth, rollerWidth, sign, fixed, y } = args;
    const half = Math.max(0, boardWidth / 2 - rollerWidth / 2);
    const lo = sign < 0 ? -half : 0;
    const hi = sign < 0 ? 0 : half;
    const axisVec: SerializableVector3 = axis === "z" ? [0, 0, 1] : [1, 0, 0];
    const min: SerializableVector3 =
      axis === "z" ? [fixed, y, lo] : [lo, y, fixed];
    const max: SerializableVector3 =
      axis === "z" ? [fixed, y, hi] : [hi, y, fixed];
    const center: SerializableVector3 =
      axis === "z" ? [fixed, y, 0] : [0, y, fixed];
    return {
      type: "edge",
      axis: axisVec,
      bounds: { min, max },
      snapAnchors: [{ position: center, label: "Center" }],
      sourceOptionUuid: boardUuid,
      optimistic: true,
    };
  }

  /** Activate-only constraint: ⌘/Ctrl-click opens the board popup, no movement. */
  private static boardActivateConstraint(
    boardUuid: string,
    position: THREE.Vector3,
  ): DragConstraint {
    const at: SerializableVector3 = [position.x, position.y, position.z];
    return {
      type: "surface",
      planeNormal: [0, 1, 0],
      bounds: { min: at, max: at },
      snapAnchors: [],
      sourceOptionUuid: boardUuid,
      activateOnly: true,
    };
  }

  private getRollerBoardOptions(
    settings: FlightCaseSettings,
  ): RollerBoardInfos[] {
    return settings.variableOptions.filter(
      (option): option is RollerBoardInfos =>
        option.variableOptionType === VariableOptionType.ROLLER_BOARD,
    );
  }

  // Temporary fallback so existing cases with exterior rollbrett keep working.
  private createLegacyRollerBoardOption(
    settings: FlightCaseSettings,
  ): RollerBoardInfos | null {
    const legacyRoller = settings.exteriorOptions.rollers;
    if (legacyRoller.rollerType !== ROLLER_TYPE.CREEPER) {
      return null;
    }

    const creeperRoller = legacyRoller as CreeperRollerType;
    // Bundle exterior data is hydrated in FlightCaseDefinition (per-wheel catalog rows on __hydratedWheelA/B).
    const legacyRollerDistanceFromEdge =
      (creeperRoller as { distanceFromEdge?: number }).distanceFromEdge ?? 27;
    return {
      layoutMode: "AUTO",
      rollerA: getHydratedLegacyCreeperWheel(creeperRoller, "A"),
      rollerB: getHydratedLegacyCreeperWheel(creeperRoller, "B"),
      singlePanelDepthInsetMm: 12,
      singlePanelSideInsetMm: 62,
      splitThresholdMm: 650,
      rollerDistanceFromEdge: legacyRollerDistanceFromEdge,
      uuid: "legacy_rollbrett",
      variableOptionType: VariableOptionType.ROLLER_BOARD,
    };
  }

  /**
   * How many boards a split layout emits. Clamped to 2–4; anything missing or
   * out of range falls back to 2, which reproduces the previous geometry
   * exactly for configurations saved before the field existed.
   */
  private resolveSplitPanelCount(option: RollerBoardInfos): number {
    const count = Number(option.splitPanelCount);
    if (!Number.isFinite(count)) return 2;
    return Math.min(4, Math.max(2, Math.round(count)));
  }

  private shouldUseSplitPanels(
    width: number,
    depth: number,
    option: RollerBoardInfos,
  ): boolean {
    switch (option.layoutMode) {
      case "SPLIT_PANEL":
        return true;
      case "SINGLE_PANEL":
        return false;
      default: {
        const threshold =
          Number.isFinite(option.splitThresholdMm) &&
          option.splitThresholdMm > 0
            ? option.splitThresholdMm
            : 650;
        return width >= threshold || depth >= threshold;
      }
    }
  }

  private getRollerBoardIdPrefix(
    option: RollerBoardInfos,
    index: number,
  ): string {
    const source = option.uuid || `roller_board_${index}`;
    return source.replace(/[^a-zA-Z0-9_-]/g, "_");
  }

  private resolveRoller(
    roller: CreeperRollerType,
    slot: "A" | "B" = "A",
  ): CreeperRollerType {
    if (isCreeperBundleMeshes(roller)) {
      const nested = slot === "B" ? roller.rollerB : roller.rollerA;
      const fromNested = resolveSingleCasterFromNested(
        nested,
        OptionDBInstance,
      );
      if (fromNested) {
        return mergeCreeperCasterWithSourceMetadata(
          buildCreeperResolutionSource(roller, nested, slot),
          fromNested,
        );
      }
      console.warn(
        "RollerBuildStep: creeper bundle passed where a single caster was expected",
        roller.name,
      );
      return roller;
    }

    const name = roller.name?.replace(/\s/g, "");
    if (!name) return roller;
    const all =
      OptionDBInstance.getAllOfKind<RollerType>(LTCaseUserOption.RollerType) ??
      [];
    const match = all.find(
      (r): r is CreeperRollerType =>
        r.rollerType === ROLLER_TYPE.CREEPER &&
        r.name?.replace(/\s/g, "") === name,
    );
    if (match) return mergeCreeperCasterWithSourceMetadata(roller, match);
    return roller;
  }

  private resolveRollbrettBoard(
    settings: FlightCaseSettings,
    rollerA: CreeperRollerType,
    rollerB: CreeperRollerType,
  ): RollerBoardProperties {
    if (rollerA.board && typeof rollerA.board === "object") {
      return rollerA.board;
    }
    const exterior = settings.exteriorOptions.rollers as CreeperRollerType;
    if (
      exterior?.rollerType === ROLLER_TYPE.CREEPER &&
      exterior.board &&
      typeof exterior.board === "object"
    ) {
      return exterior.board;
    }
    if (rollerB.board && typeof rollerB.board === "object") {
      return rollerB.board;
    }

    const braked = this.inferRollbrettBraked(rollerA, exterior);
    console.warn(
      "RollerBuildStep: missing rollbrett board configuration; using defaults",
    );
    return {
      braked,
      distanceFromEdge: 27,
      matCode: braked ? "1001b" : "1000b",
      strength: 18,
      width: 120,
    };
  }

  private inferRollbrettBraked(
    rollerA: CreeperRollerType,
    exterior: CreeperRollerType | undefined,
  ): boolean {
    if (typeof exterior?.board?.braked === "boolean") {
      return exterior.board.braked;
    }
    const label = `${rollerA.name ?? ""} ${exterior?.name ?? ""}`.toLowerCase();
    if (label.includes("ungebremst")) {
      return false;
    }
    if (label.includes("gebremst")) {
      return true;
    }
    return false;
  }

  // Helper: handle creeper type rollers
  private handleCreeperType(
    blueprint: FlightCaseBlueprint,
    _settings: FlightCaseSettings,
    sizeHelper: RectSizeHelper,
    option: RollerBoardInfos,
    idPrefix: string,
    dependencies: Map<string, RollerStepDependencyData>,
  ) {
    const width = sizeHelper.size.x;
    const depth = sizeHelper.size.y;
    const rollerA = this.resolveRoller(option.rollerA, "A");
    const rollerB =
      option.useDifferentRollers === false
        ? rollerA
        : this.resolveRoller(option.rollerB, "B");
    const board = this.resolveRollbrettBoard(_settings, rollerA, rollerB);
    const smallerDim =
      blueprint.outerDimensions.x > blueprint.outerDimensions.z ? "z" : "x";
    const boardWidth = blueprint.outerDimensions[smallerDim];

    // Only real (editable) roller boards get drag/click affordances; the
    // synthesized legacy board has no variableOption to map drag changes onto.
    const boardUuid = option.uuid;
    const enableDrag = boardUuid !== RollerBuildStep.LEGACY_BOARD_UUID;

    const panelMaterial = this.createPanelMaterial(board, rollerA);
    const holzbraunColor = OptionDBInstance.getOrThrow<PanelColor>(
      LTCaseUserOption.PanelColor,
      { name: "schokobraun (RAL8017)" },
    );

    if (this.shouldUseSplitPanels(width, depth, option)) {
      this.handleDoublePanels({
        idPrefix,
        blueprint,
        board,
        rollerA,
        rollerB,
        boardWidth,
        width,
        depth,
        panelMaterial,
        holzbraunColor,
        dependencies,
        option,
        boardUuid,
        enableDrag,
      });
    } else {
      this.handleSinglePanel({
        idPrefix,
        option,
        blueprint,
        board,
        rollerA,
        rollerB,
        width,
        depth,
        sizeHelper,
        panelMaterial,
        holzbraunColor,
        dependencies,
        boardUuid,
        enableDrag,
      });
    }
  }

  // Helper: create panel for double panels
  private createDoublePanel(args: {
    idPrefix: string;
    blueprint: FlightCaseBlueprint;
    board: RollerBoardProperties;
    boardWidth: number;
    panelMaterial: TrayMaterialType;
    holzbraunColor: PanelColor;
    p2: number;
    n: number;
    panelCounter: number;
    boardUuid: string;
    enableDrag: boolean;
  }) {
    const {
      idPrefix,
      blueprint,
      board,
      boardWidth,
      panelMaterial,
      holzbraunColor,
      p2,
      n,
      panelCounter,
      boardUuid,
      enableDrag,
    } = args;
    const panelId = `${idPrefix}_rollerpanel_double_${n}_${panelCounter}`;
    const isWide = blueprint.innerDimensions.x > blueprint.innerDimensions.z;
    const panelPosition = isWide
      ? new THREE.Vector3(
          p2,
          -blueprint.outerDimensions.y / 2 - board.strength / 2,
          0,
        )
      : new THREE.Vector3(
          0,
          -blueprint.outerDimensions.y / 2 - board.strength / 2,
          p2,
        );
    // size.x → world X, size.y → world Z for BOTTOM in SupplyPartPanelInteraction bbox.
    // Rollers sit along Z when isWide (posA/posB on Z), along X when narrow — match half-spans.
    const panelSize = isWide
      ? new THREE.Vector2(board.width, boardWidth)
      : new THREE.Vector2(boardWidth, board.width);

    const dragConstraint = enableDrag
      ? RollerBuildStep.boardActivateConstraint(boardUuid, panelPosition)
      : undefined;

    return this.createPanelDependency({
      panelId,
      panelMaterial,
      holzbraunColor,
      board,
      panelSize,
      panelPosition,
      n,
      dragConstraint,
    });
  }

  // Helper: create rollers for double panels
  private createDoublePanelRollers(args: {
    idPrefix: string;
    blueprint: FlightCaseBlueprint;
    board: RollerBoardProperties;
    rollerA: CreeperRollerType;
    rollerB: CreeperRollerType;
    posA: number;
    posB: number;
    p2: number;
    n: number;
    rollerCounter: number;
    boardUuid: string;
    enableDrag: boolean;
    boardWidth: number;
    rollerAWidth: number;
    rollerBWidth: number;
  }) {
    const {
      idPrefix,
      blueprint,
      board,
      rollerA,
      rollerB,
      posA,
      posB,
      p2,
      n,
      rollerCounter,
      boardUuid,
      enableDrag,
      boardWidth,
      rollerAWidth,
      rollerBWidth,
    } = args;
    const results: RollerDependencyData[] = [];

    const isWide = blueprint.innerDimensions.x > blueprint.innerDimensions.z;
    const y = -blueprint.outerDimensions.y / 2 - board.strength;
    // Split panels spread rollers along the board-width axis (Z when wide).
    const dragAxis: "x" | "z" = isWide ? "z" : "x";

    // Build the 1D edge constraint for a split roller (sign-limited to its half).
    const edgeContext = (rollerWidth: number) => ({
      layout: "SPLIT" as const,
      axis: dragAxis,
      boardWidth,
      rollerWidth,
    });
    const edgeConstraint = (sign: number, rollerWidth: number) =>
      enableDrag
        ? RollerBuildStep.rollerEdgeConstraint({
            boardUuid,
            axis: dragAxis,
            boardWidth,
            rollerWidth,
            sign,
            fixed: p2,
            y,
          })
        : undefined;

    // Create roller A (negative side of the board-width axis)
    const rollerAId = `${idPrefix}_roller_double_${n - 1}_A_${rollerCounter}`;
    const rollerAPosition = isWide
      ? new THREE.Vector3(p2, y, posA)
      : new THREE.Vector3(posA, y, p2);

    results.push(
      this.createRollerDependency(
        rollerAId,
        rollerA,
        rollerAPosition,
        Sides.BOTTOM,
        undefined,
        edgeConstraint(-1, rollerAWidth),
        enableDrag ? edgeContext(rollerAWidth) : undefined,
      ),
    );

    // Create roller B (positive side of the board-width axis)
    const rollerBId = `${idPrefix}_roller_double_${n - 1}_B_${rollerCounter}`;
    const rollerBPosition = isWide
      ? new THREE.Vector3(p2, y, posB)
      : new THREE.Vector3(posB, y, p2);

    results.push(
      this.createRollerDependency(
        rollerBId,
        rollerB,
        rollerBPosition,
        Sides.BOTTOM,
        undefined,
        edgeConstraint(1, rollerBWidth),
        enableDrag ? edgeContext(rollerBWidth) : undefined,
      ),
    );

    return results;
  }

  // Helper: handle double panels
  private handleDoublePanels(args: {
    idPrefix: string;
    blueprint: FlightCaseBlueprint;
    board: RollerBoardProperties;
    rollerA: CreeperRollerType;
    rollerB: CreeperRollerType;
    boardWidth: number;
    width: number;
    depth: number;
    panelMaterial: TrayMaterialType;
    holzbraunColor: PanelColor;
    dependencies: Map<string, RollerStepDependencyData>;
    option: RollerBoardInfos;
    boardUuid: string;
    enableDrag: boolean;
  }) {
    const {
      idPrefix,
      blueprint,
      board,
      rollerA,
      rollerB,
      boardWidth,
      width,
      depth,
      panelMaterial,
      holzbraunColor,
      dependencies,
      option,
      boardUuid,
      enableDrag,
    } = args;

    // Match legacy RollerBuilder: long-axis half-offset from bottom-rect sizeHelper, not max(inner).
    const pos =
      (blueprint.innerDimensions.x > blueprint.innerDimensions.z
        ? width
        : depth) /
        2 -
      board.distanceFromEdge -
      board.width / 2;

    // Extract roller properties from the roller options
    // Width belongs to individual rollers, distance from edge belongs to the roller board
    // Width can be stored directly on the CreeperRollerType or in nested rollerA/rollerB properties
    const rollerAWidth = rollerA.width ?? rollerA.rollerA?.width;
    const rollerBWidth = rollerB.width ?? rollerB.rollerB?.width;
    const rollerDistanceFromEdge = option.rollerDistanceFromEdge;

    if (
      rollerAWidth === undefined ||
      rollerBWidth === undefined ||
      rollerDistanceFromEdge === undefined
    ) {
      return;
    }

    const posA = -(boardWidth / 2 - rollerDistanceFromEdge - rollerAWidth / 2);
    const posB = boardWidth / 2 - rollerDistanceFromEdge - rollerBWidth / 2;

    let panelCounter = 0;
    let rollerCounter = 0;
    let n = 0;

    // Boards spread evenly between the two outermost positions, so a count of
    // 2 keeps exactly the previous placement and 3+ fill the span in between.
    const panelCount = this.resolveSplitPanelCount(option);
    const positions = Array.from({ length: panelCount }, (_, index) =>
      panelCount === 1 ? 0 : -pos + ((2 * pos) / (panelCount - 1)) * index,
    );

    for (const p2 of positions) {
      const panelDep = this.createDoublePanel({
        idPrefix,
        blueprint,
        board,
        boardWidth,
        panelMaterial,
        holzbraunColor,
        p2,
        n,
        panelCounter: panelCounter,
        boardUuid,
        enableDrag,
      });
      panelCounter++;
      dependencies.set(panelDep.partId, panelDep);
      n++;

      const createdRollers = this.createDoublePanelRollers({
        idPrefix,
        blueprint,
        board,
        rollerA,
        rollerB,
        posA,
        posB,
        p2,
        n,
        rollerCounter,
        boardUuid,
        enableDrag,
        boardWidth,
        rollerAWidth,
        rollerBWidth,
      });
      for (const rollerDep of createdRollers) {
        dependencies.set(rollerDep.partId, rollerDep);
        rollerCounter++;
      }
    }
  }

  // Helper: create single panel with 4 rollers
  private handleSinglePanel(args: {
    idPrefix: string;
    option: RollerBoardInfos;
    blueprint: FlightCaseBlueprint;
    board: RollerBoardProperties;
    rollerA: CreeperRollerType;
    rollerB: CreeperRollerType;
    width: number;
    depth: number;
    sizeHelper: RectSizeHelper;
    panelMaterial: TrayMaterialType;
    holzbraunColor: PanelColor;
    dependencies: Map<string, RollerStepDependencyData>;
    boardUuid: string;
    enableDrag: boolean;
  }) {
    const {
      idPrefix,
      option,
      blueprint,
      board,
      rollerA,
      rollerB,
      width,
      depth,
      sizeHelper,
      panelMaterial,
      holzbraunColor,
      dependencies,
      boardUuid,
      enableDrag,
    } = args;
    let panelCounter = 0;
    let rollerCounter = 0;

    const panelId = `${idPrefix}_rollerpanel_single_${panelCounter++}`;
    // Legacy RollerBuilder: center single board on bottom rect (pos3d), not case origin.
    const planZ = sizeHelper.pos3d.z;
    const panelPosition = new THREE.Vector3(
      0,
      -blueprint.outerDimensions.y / 2 - board.strength / 2,
      planZ,
    );
    // Same as double panel: bbox uses size.x on X, size.y on Z; rollers span depth on Z.
    const panelSize = new THREE.Vector2(width, depth);

    const panelDep = this.createPanelDependency({
      panelId,
      panelMaterial,
      holzbraunColor,
      board,
      panelSize,
      panelPosition,
      n: 0,
      dragConstraint: enableDrag
        ? RollerBuildStep.boardActivateConstraint(boardUuid, panelPosition)
        : undefined,
    });
    dependencies.set(panelId, panelDep);

    // Extract roller properties from the roller options
    // Width belongs to individual rollers
    // Width can be stored directly on the CreeperRollerType or in nested rollerA property
    const rollerAWidth = rollerA.width ?? rollerA.rollerA?.width;

    if (rollerAWidth === undefined) {
      return;
    }

    // Logic ported from Old Code:
    // It uses specific offsets (12mm and 62mm) and calculates based on rollerA width.
    // It places one 'A' and one 'B' roller for each side (Left/Right).

    const y = -blueprint.outerDimensions.y / 2 - board.strength;
    // Single-panel rollers move freely on the bottom surface (2D). Each roller
    // is bound to its own quadrant so the symmetric inset mapping round-trips.
    const surfaceContext = () => ({
      layout: "SINGLE" as const,
      width,
      depth,
      planZ,
      rollerWidth: rollerAWidth,
    });
    const surfaceConstraint = (signX: number, signZ: number) =>
      enableDrag
        ? RollerBuildStep.rollerSurfaceConstraint({
            boardUuid,
            width,
            depth,
            planZ,
            y,
            rollerWidth: rollerAWidth,
            signX,
            signZ,
          })
        : undefined;

    for (const sign of [-1, 1]) {
      // Old code calculations:
      // Note: Uses rollerAWidth for alignment of both rollers
      const posA_Z = -(
        depth / 2 -
        rollerAWidth / 2 -
        option.singlePanelDepthInsetMm
      );
      const posB_Z =
        depth / 2 - rollerAWidth / 2 - option.singlePanelDepthInsetMm;
      const posX =
        sign * (width / 2 - rollerAWidth / 2 - option.singlePanelSideInsetMm);

      // --- Roller A (negative depth side) ---
      const rollerIdA = `${idPrefix}_roller_single_A_${sign === -1 ? "L" : "R"}_${rollerCounter++}`;
      const positionA = new THREE.Vector3(posX, y, posA_Z + planZ);

      dependencies.set(
        rollerIdA,
        this.createRollerDependency(
          rollerIdA,
          rollerA,
          positionA,
          Sides.BOTTOM,
          undefined,
          surfaceConstraint(sign, -1),
          enableDrag ? surfaceContext() : undefined,
        ),
      );

      // --- Roller B (positive depth side) ---
      const rollerIdB = `${idPrefix}_roller_single_B_${sign === -1 ? "L" : "R"}_${rollerCounter++}`;
      const positionB = new THREE.Vector3(posX, y, posB_Z + planZ);

      dependencies.set(
        rollerIdB,
        this.createRollerDependency(
          rollerIdB,
          rollerB,
          positionB,
          Sides.BOTTOM,
          undefined,
          surfaceConstraint(sign, 1),
          enableDrag ? surfaceContext() : undefined,
        ),
      );
    }
  }

  // Helper: safe numeric value for position calculation (avoids NaN from undefined/serialized data)
  private static safeDim(
    value: number | undefined,
    fallback: number,
    label: string,
    debug: { invalid: string[] },
  ): number {
    const n = Number(value);
    if (!Number.isFinite(n)) {
      debug.invalid.push(`${label}=${String(value)}`);
      return fallback;
    }
    return n;
  }

  private static isHorizontalMountSide(side: Sides): boolean {
    return (
      side === Sides.LEFT ||
      side === Sides.RIGHT ||
      side === Sides.FRONT ||
      side === Sides.BACK
    );
  }

  private static edgeSideTag(side: Sides): string {
    switch (side) {
      case Sides.LEFT:
        return "L";
      case Sides.RIGHT:
        return "R";
      case Sides.FRONT:
        return "F";
      case Sides.BACK:
        return "B";
      default:
        return String(side);
    }
  }

  /** Same basis as links/rechts: rotateOntoSide + 180° X (legacy RollerBuilder). */
  private edgeRollerRotation(side: Sides): THREE.Euler {
    const base = this.rotateOntoSide(side, 0);
    let z = base.z;
    if (side === Sides.FRONT || side === Sides.BACK) {
      // Front/back need extra 180° so bracket hugs edge (not pointing away).
      z += Math.PI;
    }
    return new THREE.Euler(base.x - Math.PI, base.y, z);
  }

  // Helper: handle edge type rollers (Kantenrolle on LEFT/RIGHT/FRONT/BACK)
  private handleEdgeType(
    blueprint: FlightCaseBlueprint,
    settings: FlightCaseSettings,
    dependencies: Map<string, RollerStepDependencyData>,
  ) {
    const rollers = settings.exteriorOptions.rollers as EdgeRollerType;
    const { side } = rollers;
    // Sides.LEFT is 0 — must not use `!side` (order 7723, Kantenrolle links).
    if (!RollerBuildStep.isHorizontalMountSide(side)) {
      console.warn(
        "RollerBuildStep: ROLLER_TYPE.EDGE requires side LEFT, RIGHT, FRONT, or BACK. Skipping.",
      );
      return;
    }

    const debug = { invalid: [] as string[] };
    const strength = RollerBuildStep.safeDim(
      settings.exteriorOptions?.strength,
      6.5,
      "exteriorOptions.strength",
      debug,
    );
    const innerX = RollerBuildStep.safeDim(
      blueprint.innerDimensions?.x,
      0,
      "innerDimensions.x",
      debug,
    );
    const innerY = RollerBuildStep.safeDim(
      blueprint.innerDimensions?.y,
      0,
      "innerDimensions.y",
      debug,
    );
    const innerZ = RollerBuildStep.safeDim(
      blueprint.innerDimensions?.z,
      0,
      "innerDimensions.z",
      debug,
    );
    if (debug.invalid.length > 0) {
      console.warn(
        "RollerBuildStep.handleEdgeType: invalid values (using fallbacks):",
        debug.invalid.join(", "),
        {
          innerDimensions: blueprint.innerDimensions,
          strength: settings.exteriorOptions?.strength,
        },
      );
    }

    const edgeInset = strength + 1.5;
    const alongInset = strength - 70;
    const y = -innerY / 2 - strength - 1.5;
    const rotation = this.edgeRollerRotation(side);
    const sideTag = RollerBuildStep.edgeSideTag(side);

    let rollerCounter = 0;
    for (let i = 0; i < 2; i++) {
      const alongSign = i === 0 ? -1 : 1;
      let position: THREE.Vector3;
      if (side === Sides.LEFT || side === Sides.RIGHT) {
        const xSign = side === Sides.RIGHT ? 1 : -1;
        position = new THREE.Vector3(
          xSign * (innerX / 2 + edgeInset),
          y,
          alongSign * (innerZ / 2 + alongInset),
        );
      } else {
        const zSign = side === Sides.FRONT ? 1 : -1;
        position = new THREE.Vector3(
          alongSign * (innerX / 2 + alongInset),
          y,
          zSign * (innerZ / 2 + edgeInset),
        );
      }

      const rollerId = `roller_edge_${sideTag}_${i}_${rollerCounter++}`;
      const rollerDep = this.createRollerDependency(
        rollerId,
        rollers,
        position,
        side,
        rotation,
      );
      dependencies.set(rollerId, rollerDep);
    }
  }

  public selectDependencies(
    blueprint: FlightCaseBlueprint,
    settings: FlightCaseSettings,
  ): Map<string, RollerStepDependencyData> {
    const dependencies = new Map<string, RollerStepDependencyData>();

    const baseData = this.getBaseRectAndSizeHelper(blueprint, settings);
    if (!baseData) {
      return dependencies;
    }

    const rollerBoardOptions = this.getRollerBoardOptions(settings);
    if (rollerBoardOptions.length > 0) {
      rollerBoardOptions.forEach((option, index) => {
        this.handleCreeperType(
          blueprint,
          settings,
          baseData.sizeHelper,
          option,
          this.getRollerBoardIdPrefix(option, index),
          dependencies,
        );
      });
    } else {
      const legacyOption = this.createLegacyRollerBoardOption(settings);
      if (legacyOption) {
        this.handleCreeperType(
          blueprint,
          settings,
          baseData.sizeHelper,
          legacyOption,
          "legacy_rollbrett",
          dependencies,
        );
      }
    }

    const { rollers } = settings.exteriorOptions;
    switch (rollers.rollerType) {
      case ROLLER_TYPE.NONE:
      case ROLLER_TYPE.CREEPER:
      case ROLLER_TYPE.CORNER_INBOARD:
      case ROLLER_TYPE.CORNER_OUTBOARD:
        break;
      case ROLLER_TYPE.EDGE:
        this.handleEdgeType(blueprint, settings, dependencies);
        break;
    }

    return dependencies;
  }

  public createPart(
    partId: string,
    dependencies: RollerStepDependencyData,
  ): RollerDefinition | RollerPanelProperties {
    switch (dependencies.partKind) {
      case "RollerPanel": {
        // Rotation for roller panels is constant
        const finalRotation = this.rotateOntoSide(Sides.BOTTOM, 0);
        return {
          ...dependencies, // Includes type, panelType, material, color, strength, note, braked, size, position, side, n
          id: partId,
          group: dependencies.groupName, // Add group explicitly
          buildstepName: dependencies.buildstepName, // Add buildstepName explicitly
          cutouts: [], // Initialize cutouts
          orientation: 0, // size order matches world XZ footprint (no extra Z-rot vs bbox)
          rotation: finalRotation,
        };
      }
      case "Roller": {
        // Rotation is pre-calculated for EDGE type, defaults for CREEPER type
        const finalRotation = dependencies.rotation
          ? dependencies.rotation.clone()
          : this.rotateOntoSide(Sides.BOTTOM, 0);
        return {
          id: partId,
          ...dependencies, // Includes type, partSelection, position, side, isSupplyPart
          group: dependencies.groupName, // Add group explicitly
          buildstepName: dependencies.buildstepName, // Add buildstepName explicitly
          orientation: 90, // fixiate orientation to 90°
          rotation: finalRotation,
        } as RollerDefinition;
      }
      default: {
        const exhaustiveCheck: never = dependencies;
        throw new Error(`Unknown part kind: ${exhaustiveCheck}`);
      }
    }
  }

  /**
   * Convert a roller drag into roller board settings.
   * - SINGLE panel (2D surface): updates side + depth insets.
   * - SPLIT panel (1D edge): updates rollerDistanceFromEdge.
   * All rollers are derived symmetrically from these shared parameters, so
   * moving one roller mirrors the others. Panel parts are activate-only and
   * return null (click opens the popup; no movement).
   */
  public updateSettingsFromDrag(
    _partId: string,
    newPosition: THREE.Vector3,
    constraint: DragConstraint,
    settingsContext: Record<string, unknown> | undefined,
    currentSettings: FlightCaseSettings,
    options?: DragUpdateOptions,
  ): DragUpdateResult<FlightCaseSettings> | null {
    const layout = settingsContext?.layout;
    if (
      !constraint.sourceOptionUuid ||
      (layout !== "SINGLE" && layout !== "SPLIT")
    ) {
      return null;
    }

    const gridStepMm = gridStepFromOptions(options);

    const optionIndex = currentSettings.variableOptions.findIndex(
      (opt) => opt.uuid === constraint.sourceOptionUuid,
    );
    if (optionIndex === -1) {
      console.warn(
        `RollerBuildStep: no variableOption with UUID ${constraint.sourceOptionUuid}`,
      );
      return null;
    }
    const option = currentSettings.variableOptions[
      optionIndex
    ] as RollerBoardInfos;
    const updatedVariableOptions = [...currentSettings.variableOptions];

    if (layout === "SINGLE") {
      const width = Number(settingsContext?.width) || 0;
      const depth = Number(settingsContext?.depth) || 0;
      const planZ = Number(settingsContext?.planZ) || 0;
      const rollerWidth = Number(settingsContext?.rollerWidth) || 0;

      const sideInset = roundToDragGrid(
        Math.max(0, width / 2 - rollerWidth / 2 - Math.abs(newPosition.x)),
        gridStepMm,
      );
      const depthInset = roundToDragGrid(
        Math.max(
          0,
          depth / 2 - rollerWidth / 2 - Math.abs(newPosition.z - planZ),
        ),
        gridStepMm,
      );

      const snappedPosition = newPosition.clone();
      snappedPosition.x =
        Math.sign(newPosition.x || 1) *
        (width / 2 - rollerWidth / 2 - sideInset);
      const zFromPlan = newPosition.z - planZ;
      snappedPosition.z =
        planZ +
        Math.sign(zFromPlan || 1) * (depth / 2 - rollerWidth / 2 - depthInset);

      updatedVariableOptions[optionIndex] = {
        ...option,
        singlePanelSideInsetMm: sideInset,
        singlePanelDepthInsetMm: depthInset,
      };

      return {
        settingsUpdate: { variableOptions: updatedVariableOptions },
        displayValues: {
          primary: { label: "Seite", value: sideInset, unit: "mm" },
          secondary: { label: "Tiefe", value: depthInset, unit: "mm" },
        },
        snappedPosition: [
          snappedPosition.x,
          snappedPosition.y,
          snappedPosition.z,
        ],
      };
    }

    // SPLIT: rollers move along a single board-width axis.
    const axis = settingsContext?.axis === "x" ? "x" : "z";
    const boardWidth = Number(settingsContext?.boardWidth) || 0;
    const rollerWidth = Number(settingsContext?.rollerWidth) || 0;
    const along = axis === "x" ? newPosition.x : newPosition.z;
    const distance = roundToDragGrid(
      Math.max(0, boardWidth / 2 - rollerWidth / 2 - Math.abs(along)),
      gridStepMm,
    );

    const snappedPosition = newPosition.clone();
    const alongPos =
      Math.sign(along || 1) * (boardWidth / 2 - rollerWidth / 2 - distance);
    if (axis === "x") {
      snappedPosition.x = alongPos;
    } else {
      snappedPosition.z = alongPos;
    }

    updatedVariableOptions[optionIndex] = {
      ...option,
      rollerDistanceFromEdge: distance,
    };

    return {
      settingsUpdate: { variableOptions: updatedVariableOptions },
      displayValues: {
        primary: { label: "Randabstand", value: distance, unit: "mm" },
      },
      snappedPosition: [
        snappedPosition.x,
        snappedPosition.y,
        snappedPosition.z,
      ],
    };
  }
}
